/**
 * Brush definitions. Brushes decide, for a given pointer pressure and base
 * size, the effective width, opacity, color, and how strokes are composited.
 *
 * Mirrors the tools found in Endless Paper / InfiniPaint / infinite-zoom
 * apps: pencil, pen (ink), marker, highlighter, eraser.
 */

import { EraserMode, StrokeStyle, ToolKind } from "./model";

export interface BrushDef {
  tool: ToolKind;
  id: string;
  name: string;
  icon: string;
  /** Default base size in "screen units" (px at scale 1). */
  defaultSize: number;
  minSize: number;
  maxSize: number;
  defaultColor: string;
  defaultOpacity: number;
  /** Size multiplier from pressure (0..1 pressure -> multiplier). */
  pressureSize: (p: number) => number;
  opacityCurve: (p: number) => number;
  eraserMode?: EraserMode;
  /** Cap style applied at stroke ends. */
  capScale: number;
}

const lin = (min: number, max: number) => (p: number) => min + (max - min) * p;

export const BRUSHES: BrushDef[] = [
  {
    tool: "pencil",
    id: "pencil",
    name: "Pencil",
    icon: "✏️",
    defaultSize: 2.5,
    minSize: 0.5,
    maxSize: 20,
    defaultColor: "#3a3a3a",
    defaultOpacity: 0.9,
    pressureSize: lin(0.35, 1),
    opacityCurve: lin(0.55, 1),
    capScale: 1,
  },
  {
    tool: "pen",
    id: "pen",
    name: "Pen",
    icon: "🖊️",
    defaultSize: 3.5,
    minSize: 0.5,
    maxSize: 30,
    defaultColor: "#1a1a1a",
    defaultOpacity: 1,
    pressureSize: lin(0.3, 1),
    opacityCurve: lin(0.8, 1),
    capScale: 1,
  },
  {
    tool: "marker",
    id: "marker",
    name: "Marker",
    icon: "🖍️",
    defaultSize: 14,
    minSize: 2,
    maxSize: 60,
    defaultColor: "#e5484d",
    defaultOpacity: 0.55,
    pressureSize: lin(0.7, 1),
    opacityCurve: lin(0.3, 0.85),
    capScale: 1,
  },
  {
    tool: "highlighter",
    id: "highlighter",
    name: "Highlighter",
    icon: "🖍️",
    defaultSize: 18,
    minSize: 3,
    maxSize: 80,
    defaultColor: "#ffd60a",
    defaultOpacity: 0.4,
    pressureSize: lin(0.8, 1),
    opacityCurve: lin(0.25, 0.6),
    capScale: 0.9,
  },
  {
    tool: "eraser",
    id: "eraser",
    name: "Eraser",
    icon: "🧽",
    defaultSize: 24,
    minSize: 2,
    maxSize: 120,
    defaultColor: "transparent",
    defaultOpacity: 1,
    pressureSize: lin(0.5, 1),
    opacityCurve: () => 1,
    eraserMode: "current",
    capScale: 1,
  },
];

export function brushFor(tool: ToolKind): BrushDef {
  const b = BRUSHES.find((b) => b.tool === tool);
  if (!b) throw new Error(`No brush for tool ${tool}`);
  return b;
}

export interface ActiveStyle {
  tool: ToolKind;
  color: string;
  size: number; // base size in *cell-local* units at the current zoom
  opacity: number;
  eraserMode?: EraserMode;
}

export function toStrokeStyle(active: ActiveStyle): StrokeStyle {
  return {
    tool: active.tool as StrokeStyle["tool"],
    color: active.color,
    size: active.size,
    opacity: active.opacity,
    eraserMode: active.eraserMode,
  };
}
